import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { MdDelete, MdEdit } from "react-icons/md";
import { XMarkIcon, CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useBrandHook } from "./useHook";
import Loader, { ImageWithFallback } from "../../Utils/loader";

function BrandManagement() {
  const { getBrands, addBrand, updateBrand, deleteBrand } = useBrandHook();

  const [brands, setBrands] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [mainLoading, setMainLoading] = useState(true);

  const [formData, setFormData] = useState({
    file: null,
    name: "",
    preview: null,
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setMainLoading(true);
      await getBrands(setBrands);
    } catch (error) {
      console.error("Error fetching brands:", error);
    } finally {
      setMainLoading(false);
    }
  };

  const openModal = (index = null) => {
    if (index !== null && brands[index]) {
      const brandToEdit = brands[index];
      setFormData({
        file: null,
        name: brandToEdit.name,
        preview: brandToEdit.image,
      });
      setEditIndex(index);
    } else {
      setFormData({ file: null, name: "", preview: null });
      setEditIndex(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (formData.preview && formData.preview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.preview);
    }

    const fileInput = document.getElementById('logo-upload');
    if (fileInput) {
      fileInput.value = '';
    }

    setIsModalOpen(false);
    setEditIndex(null);
    setSubmitting(false);
    setFormData({ file: null, name: "", preview: null });
  };

  const handleAddBrand = async () => {
    if (!formData.file || !formData.name.trim()) {
      Swal.fire("Error!", "Please fill in all required fields.", "error");
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(formData.name.trim())) {
      Swal.fire("Error!", "Brand name can only contain letters and spaces.", "error");
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("image", formData.file);
      fd.append("name", formData.name.trim());

      await addBrand(fd);
      Swal.fire("Success!", "Brand added successfully.", "success");
      await fetchBrands();
      closeModal();
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const isFormValid = () => {
    const nameValid = formData.name.trim().length > 0 && /^[A-Za-z\s]+$/.test(formData.name.trim());
    // For new brands, file is required. For updates, either file or existing preview is acceptable
    const imageValid = editIndex === null ? formData.file : (formData.file || formData.preview);
    return nameValid && imageValid;
  };

  const validateBrandName = (name) => {
    return /^[A-Za-z\s]*$/.test(name);
  };

  const handleUpdateBrand = async () => {
    if (!formData.name.trim()) {
      Swal.fire("Error!", "Please enter a brand name.", "error");
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(formData.name.trim())) {
      Swal.fire("Error!", "Brand name can only contain letters and spaces.", "error");
      return;
    }

    // For updates, we need either a new file or the existing image
    if (!formData.file && !formData.preview) {
      Swal.fire("Error!", "Please provide a brand logo.", "error");
      return;
    }
    try {
      setSubmitting(true);
      const brandId = brands[editIndex].id;
      const fd = new FormData();
      if (formData.file) {
        fd.append("image", formData.file);
      }
      fd.append("name", formData.name.trim());

      await updateBrand(brandId, fd);
      Swal.fire("Success!", "Brand updated successfully.", "success");
      await fetchBrands();
      closeModal();
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBrand = (index) => {
    const brandId = brands[index].id;
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setDeletingIndex(index);
          await deleteBrand(brandId);
          Swal.fire("Deleted!", "Brand has been deleted.", "success");
          await fetchBrands();
        } catch (error) {
          Swal.fire("Error!", error.message, "error");
        } finally {
          setDeletingIndex(null);
        }
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        Swal.fire("Error!", "Please select a valid image file.", "error");
        e.target.value = '';
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        Swal.fire("Error!", "Logo size should be less than 2MB.", "error");
        e.target.value = '';
        return;
      }

      const img = new Image();
      const preview = URL.createObjectURL(file);

      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const minDimension = Math.min(img.width, img.height);

        if (minDimension < 100 || aspectRatio < 0.5 || aspectRatio > 2) {
          Swal.fire({
            title: "Invalid Image Dimensions",
            text: "Please use a square or near-square logo with minimum dimensions of 100x100px. Recommended: 250x250px.",
            icon: "error",
            confirmButtonText: "Choose Another Image"
          });
          URL.revokeObjectURL(preview);
          e.target.value = '';
          return;
        }

        setFormData({ ...formData, file, preview });
      };

      img.onerror = () => {
        Swal.fire("Error!", "Invalid image file.", "error");
        URL.revokeObjectURL(preview);
        e.target.value = '';
      };

      img.src = preview;
    }
  };

  const removePreviewImage = () => {
    if (formData.preview && formData.preview.startsWith('blob:')) {
      URL.revokeObjectURL(formData.preview);
    }
    setFormData({ ...formData, file: null, preview: null });

    const fileInput = document.getElementById('logo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };



  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }

    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    return imageUrl.startsWith('/') ? `${baseUrl}${imageUrl}` : `${baseUrl}/${imageUrl}`;
  };

  return (
    <div className="bg-gray-100 p-4 min-h-screen">
      <h2 className="text-2xl font-bold mb-6 text-center">Brand Management</h2>

      {mainLoading && (
        <Loader message="Loading brands..." />
      )}

      {!mainLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {brands?.map((brand, index) => (
            <div
              key={brand.id || index}
              className="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
            >
              <div className="mt-8 h-54 w-full flex items-center justify-center p-6 border-b">
                <ImageWithFallback
                  src={getImageUrl(brand.image)}
                  alt={`${brand.name} logo`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>

              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center truncate" title={brand.name}>
                  {brand.name}
                </h3>

                <div className="flex gap-2">
                  <button
                    className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors duration-200"
                    onClick={() => openModal(index)}
                  >
                    <MdEdit className="mr-1 text-sm" />
                    Edit
                  </button>
                  <button
                    className={`flex-1 flex items-center justify-center px-3 py-2 text-sm rounded transition-colors duration-200 ${deletingIndex === index
                      ? "bg-red-300 text-red-800 cursor-not-allowed"
                      : "bg-red-500 text-white hover:bg-red-600"
                      }`}
                    onClick={() => handleDeleteBrand(index)}
                    disabled={deletingIndex === index}
                  >
                    {deletingIndex === index ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4 mr-1"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8H4z"
                          ></path>
                        </svg>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <MdDelete className="mr-1 text-sm" />
                        Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!mainLoading && brands?.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No brands yet</h3>
          <p className="text-gray-500 mb-6">Get started by adding your first brand</p>
        </div>
      )}

      <div className="mt-6 text-center">
        <button
          className={`w-full sm:w-auto bg-green-500 text-white px-6 py-3 rounded shadow transition-colors ${submitting ? "opacity-50 cursor-not-allowed" : "hover:bg-green-600"
            }`}
          onClick={() => openModal(null)}
          disabled={submitting}
        >
          {submitting ? (
            <div className="flex items-center justify-center space-x-2">
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
              <span>Loading...</span>
            </div>
          ) : (
            "Add Brand"
          )}
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-2xl w-[500px] max-h-[90vh] overflow-y-auto relative">
            <XMarkIcon
              className="absolute top-4 right-4 h-6 w-6 text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
              onClick={closeModal}
            />
            <h3 className="text-2xl font-semibold mb-6 text-center text-gray-800">
              {editIndex !== null ? "Edit Brand" : "Add Brand"}
            </h3>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Brand Logo {editIndex === null && <span className="text-red-500">*</span>}
              </label>

              {formData.preview ? (
                <div className="relative">
                  <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    <ImageWithFallback
                      src={formData.preview}
                      alt="Brand logo preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={removePreviewImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                    title="Remove image"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="logo-upload"
                  className="cursor-pointer w-full h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
                  <span className="text-gray-600 font-medium">Upload Logo</span>
                  <span className="text-gray-400 text-sm mt-1">Click to browse files</span>
                </label>
              )}

              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />

              <p className="text-gray-500 text-xs">
                Required: Square logo (min 100x100px, recommended 250x250px). Max file size: 2MB
              </p>
            </div>
            <div className="space-y-2">
              <label htmlFor="brand-name" className="block text-sm font-medium text-gray-700">
                Brand Name <span className="text-red-500">*</span>
              </label>
              <input
                id="brand-name"
                type="text"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (validateBrandName(value)) {
                    setFormData({ ...formData, name: value });
                  }
                }}
                placeholder="Enter brand name (alphabets only)"
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formData.name.trim().length > 0 && !/^[A-Za-z\s]+$/.test(formData.name.trim())
                  ? 'border-red-500'
                  : ''
                  }`}
                maxLength={50}
              />
              {formData.name.trim().length > 0 && !/^[A-Za-z\s]+$/.test(formData.name.trim()) && (
                <p className="text-red-500 text-sm">Brand name can only contain letters and spaces</p>
              )}
              <p className="text-gray-500 text-xs">
                Only alphabets and spaces allowed
              </p>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition-colors"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </button>
              {editIndex !== null ? (
                <button
                  className={`px-4 py-2 rounded transition-colors ${isFormValid()
                    ? "bg-blue-500 text-white hover:bg-blue-600"
                    : "bg-gray-400 text-gray-600 cursor-not-allowed"
                    }`}
                  onClick={handleUpdateBrand}
                  disabled={submitting || !isFormValid()}
                >
                  {submitting ? "Updating..." : "Update"}
                </button>
              ) : (
                <button
                  className={`px-4 py-2 rounded transition-colors ${isFormValid()
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-gray-400 text-gray-600 cursor-not-allowed"
                    }`}
                  onClick={handleAddBrand}
                  disabled={submitting || !isFormValid()}
                >
                  {submitting ? "Adding..." : "Add"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandManagement;